import { __spreadArray, __assign } from 'tslib';
import { useSelect } from 'downshift';
import React, { useMemo, useState, cloneElement, useEffect } from 'react';
import { v4 } from 'uuid';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { noop } from '../../utils/noop.esm.js';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { Button } from '../Button/Button.esm.js';
import '@fortawesome/pro-solid-svg-icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/FaIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { Portal } from '../Portal/Portal.esm.js';
import { SelectList } from './SelectList.esm.js';
import { useSelectAll } from './useSelectAll.esm.js';
import { sortBySelected, onSelectedItemChange, isDomElement, selectedItemLabel } from './utils.esm.js';

var emptyItems = [];
/**
 * This component is like `Select` but does not handle searching in the Select at all.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectWithoutSearch } from "@panwds/react-ui";
 * ```
 */
function SelectWithoutSearch(_a) {
    var _b;
    var buttonRef = _a.buttonRef, _c = _a.items, items = _c === void 0 ? emptyItems : _c, button = _a.button, dataAutofocus = _a["data-autofocus"], dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, disabled = _a.disabled, _d = _a.defaultPlacement, defaultPlacement = _d === void 0 ? "bottom-start" : _d, _e = _a.enableSelectAll, enableSelectAll = _e === void 0 ? false : _e, _f = _a.fullWidth, fullWidth = _f === void 0 ? false : _f, _g = _a.isLoading, isLoading = _g === void 0 ? false : _g, labelId = _a.labelId, listItemStyle = _a.listItemStyle, toggleButtonId = _a.toggleButtonId, darkMode = _a.darkMode, menuStyle = _a.menuStyle, _h = _a.enableMultiSelect, enableMultiSelect = _h === void 0 ? false : _h, _j = _a.onChange, onChange = _j === void 0 ? noop : _j, onMenuKeyEnter = _a.onMenuKeyEnter, onOpen = _a.onOpen, selectedItem = _a.selectedItem;
    var baseId = useMemo(function () { return "field-".concat(v4(), "-label"); }, []);
    var selectedItems = selectedItem ? [selectedItem].flat() : [];
    var selectedValues = useMemo(function () { return selectedItems.map(function (item) { return item.value; }); }, [selectedItems]);
    var _k = useSelectAll({
        items: items,
        enableSelectAll: enableSelectAll,
        selectedValues: selectedValues,
        enableMultiSelect: enableMultiSelect
    }), showSelectAll = _k.showSelectAll, selectAllItem = _k.selectAllItem, allSelected = _k.allSelected;
    var _l = useState(__spreadArray([], items, true).sort(sortBySelected(selectedValues))), sortedItems = _l[0], setSortedItems = _l[1];
    var normalizedItems = enableSelectAll && showSelectAll
        ? __spreadArray([selectAllItem], sortedItems, true) : sortedItems;
    var _m = useSelect({
        items: normalizedItems,
        labelId: labelId,
        id: baseId,
        toggleButtonId: toggleButtonId,
        itemToString: function (item) {
            return item ? String(item.value) : "";
        },
        stateReducer: function (state, actionAndChanges) {
            var changes = actionAndChanges.changes, type = actionAndChanges.type;
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    return __assign(__assign({}, changes), { highlightedIndex: state.highlightedIndex, isOpen: enableMultiSelect });
                default:
                    return changes;
            }
        },
        selectedItem: enableMultiSelect ? null : selectedItems[0] || null,
        onSelectedItemChange: function (changes) {
            onSelectedItemChange({
                changes: changes,
                enableMultiSelect: enableMultiSelect,
                selectedItems: selectedItems,
                onChange: onChange,
                selectedValues: selectedValues,
                selectAllItem: selectAllItem,
                allSelected: allSelected,
                itemsForSelectAll: items
            });
        },
        onStateChange: function (_a) {
            var _b;
            var type = _a.type, isOpen = _a.isOpen;
            if (type === "__menu_blur__" && !isOpen)
                (_b = rawTriggerRef === null || rawTriggerRef === void 0 ? void 0 : rawTriggerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }), isOpen = _m.isOpen, getItemProps = _m.getItemProps, getMenuProps = _m.getMenuProps, highlightedIndex = _m.highlightedIndex, getToggleButtonProps = _m.getToggleButtonProps;
    var _o = usePopper({
        placement: defaultPlacement,
        strategy: "fixed"
    }), rawTriggerRef = _o.rawTriggerRef, triggerRef = _o.triggerRef, containerRef = _o.containerRef;
    var addProps = isDomElement(button === null || button === void 0 ? void 0 : button.type.toString()) ? {} : { isOpen: isOpen };
    var DefaultButton = (React.createElement(Button, { "data-autofocus": dataAutofocus, "data-testid": "select-trigger", fullWidth: fullWidth, isMenu: true, isOpen: isOpen }, selectedItemLabel(selectedItem)));
    var trigger = cloneElement(button || DefaultButton, getToggleButtonProps(__assign(__assign(__assign({}, addProps), ((_b = button === null || button === void 0 ? void 0 : button.props) !== null && _b !== void 0 ? _b : {})), { ref: buttonRef ? mergeRefs([buttonRef, triggerRef]) : triggerRef, disabled: disabled })));
    useEffect(function () {
        if (!isOpen && enableMultiSelect)
            setSortedItems(__spreadArray([], items, true).sort(sortBySelected(selectedValues)));
        if (onOpen) {
            onOpen(isOpen);
        }
    }, [isOpen]);
    useEffect(function () {
        if (enableMultiSelect) {
            setSortedItems(__spreadArray([], items, true).sort(sortBySelected(selectedValues)));
        }
        else {
            setSortedItems(__spreadArray([], items, true));
        }
    }, [items]);
    return (React.createElement("div", { className: classNames(darkMode && "dark") },
        trigger,
        React.createElement(Portal, null,
            React.createElement("div", { className: classNames("tw-z-30", darkMode && "dark"), ref: containerRef },
                React.createElement(SelectList, { dataMetrics: dataMetrics, dataTestId: dataTestId, isLoading: isLoading, isOpen: isOpen, listItemStyle: listItemStyle, menuStyle: menuStyle, getMenuProps: getMenuProps, onMenuKeyEnter: onMenuKeyEnter, items: normalizedItems, highlightedIndex: highlightedIndex, selectedItems: selectedItems, selectAllItem: selectAllItem, enableMultiSelect: enableMultiSelect, getItemProps: getItemProps, showSelectAll: showSelectAll, selectedValues: selectedValues })))));
}

export { SelectWithoutSearch };
//# sourceMappingURL=SelectWithoutSearch.esm.js.map
